import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArrowLeft, Plus, Search, Pencil, Trash2, X, ImagePlus, Eye, EyeOff, Check, PackageOpen, Loader2, ChevronDown,
} from "lucide-react";
import { RootState, AppDispatch } from "../../redux/store";
import {
  upsertProduct,
  upsertProductMedia,
  uploadProductFiles,
  deleteProductFiles,
  deleteProduct,
  deleteProductMedia,
  Product,
} from "../../redux/productSlice";
import { AllCategoriesFlat, type CategoryItem } from "../DASHBOARD/Categories";
import {
  ATTR_COLOR,
  attributesForCategory,
  type Attribute,
  type AttributeOption,
} from "../DASHBOARD/Attributes";



const DEPT_ID = "01";
const childrenOf = (parentId: string): CategoryItem[] => AllCategoriesFlat.filter((c) => c.parentId === parentId);
const byId = (id?: string) => (id ? AllCategoriesFlat.find((c) => c.id === id) : undefined);
const CATEGORIES = childrenOf(DEPT_ID);
const nameOf = (id?: string) => byId(id)?.name;
/* [category, subcategory, type] for any id */
const trailOf = (id?: string): CategoryItem[] => {
  const out: CategoryItem[] = [];
  let cur = byId(id);
  while (cur && cur.parentId) { out.unshift(cur); cur = byId(cur.parentId); }
  return out;
};
const pathLabel = (id?: string) => trailOf(id).map((n) => n.name).join(" › ");
const attrsFor = (categoryId: string): Attribute[] => attributesForCategory(categoryId);


type AttrValue = string | string[];
type AttrMap = Record<string, AttrValue>;

const optValue = (o: AttributeOption) => o.name ?? String(o.id);
const optLabel = optValue;
const swatch = (hex?: string) => (
  <span
    className={`op-swatch${hex ? "" : " multi"}`}
    style={hex ? ({ "--c": hex } as React.CSSProperties) : undefined}
  />
);

const MULTI_ATTRS = ["Color"];
const TAG_ATTRS = ["Skin Type", "Finish", "Concentration"];
const toArr = (v?: AttrValue): string[] => (Array.isArray(v) ? v : v ? [String(v)] : []);
const hexOf = (name: string) => ATTR_COLOR.options?.find((o) => o.name === name)?.hex;

const isEmpty = (v: AttrValue | undefined) => v == null || (Array.isArray(v) ? v.length === 0 : String(v).trim() === "");

/* ═══════════════════════════ STYLES: black, white and pink ═══════════════════════════ */
const INK = "#000";
const BODY = "#5C5C5C";
const LINE = "#EBE3E6";
const PINK_HOT = "#FA326E";
const PINK = "#FA2864";
const PINK_DEEP = "#FA1E5A";
const RASP = "#E60A46";
const FUCHSIA = "#DC003C";
const TINT = "#FFF3F7";
const TINT2 = "#FFE1EA";
const GRAD = `linear-gradient(135deg, ${PINK_HOT} 0%, ${PINK} 45%, ${PINK_DEEP} 100%)`;
const GRAD_DARK = `linear-gradient(135deg, ${RASP} 0%, ${FUCHSIA} 100%)`;

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600&family=Jost:wght@300;400;500;600&display=swap');
.af-page, .af-page *, .af-page *::before, .af-page *::after{ box-sizing:border-box; font-family:'Jost',system-ui,sans-serif; }
.af-page{ position:relative; min-height:100vh; overflow-x:hidden; color:${INK};
  background:
    radial-gradient(900px 420px at 105% -8%, ${TINT2} 0, rgba(255,225,234,0) 62%),
    radial-gradient(700px 360px at -10% 0%, ${TINT} 0, rgba(255,243,247,0) 60%),
    #fff;
  padding:24px 16px 90px; padding-top:calc(24px + env(safe-area-inset-top)); padding-bottom:calc(90px + env(safe-area-inset-bottom)); }
.af-page h2{ font-family:'Cormorant Garamond',serif; font-size:clamp(36px,6vw,50px); font-weight:500; letter-spacing:.01em; line-height:1.05; margin:14px 0 18px; color:${INK}; }
.af-page h2::after{ content:""; display:block; width:54px; height:3px; border-radius:999px; background:${GRAD}; margin-top:12px; }
.af-page h3{ font-family:'Cormorant Garamond',serif; font-size:26px; font-weight:500; margin:0 0 12px; color:${INK}; }
.af-page p{ margin:0; }
.af-page button{ font-family:inherit; cursor:pointer; border:none; background:none; color:inherit; }
.af-page button:disabled{ cursor:not-allowed; opacity:.5; }
.af-page button:focus-visible, .af-page input:focus-visible, .af-page textarea:focus-visible{ outline:2px solid ${PINK}; outline-offset:2px; }

.af-wrap{ position:relative; z-index:1; width:100%; max-width:640px; margin:0 auto; }
.af-wrap.op-wide{ max-width:1040px; }

.af-glass{ background:#fff; border:1px solid ${LINE}; border-radius:20px; box-shadow:0 1px 0 rgba(0,0,0,.02), 0 18px 34px -26px rgba(220,0,60,.38); }
.af-card{ padding:22px; }
.af-muted{ color:${BODY}; }
.af-small{ font-size:13px; }
.af-between{ display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.af-grow{ flex:1; min-width:0; }
.af-row{ display:grid; grid-template-columns:1fr 1fr; gap:14px; }

.af-link{ display:inline-flex; align-items:center; gap:6px; font-size:12px; font-weight:500; letter-spacing:.18em; text-transform:uppercase; color:${INK}; padding:6px 2px; transition:color .2s; }
.af-link:hover{ color:${PINK}; }

.af-btn{ display:inline-flex; align-items:center; justify-content:center; gap:8px; height:46px; padding:0 26px; border-radius:999px;
  background:${GRAD}; border:1px solid ${PINK_DEEP}; color:#fff !important; font-size:12px; font-weight:500; letter-spacing:.22em; text-transform:uppercase;
  box-shadow:0 12px 24px -14px ${FUCHSIA};
  transition:background .25s, border-color .25s, color .25s, transform .25s, box-shadow .25s; }
.af-btn:hover:not(:disabled){ background:${GRAD_DARK}; border-color:${FUCHSIA}; transform:translateY(-1px); box-shadow:0 16px 28px -14px ${FUCHSIA}; }
.af-btn:active:not(:disabled){ background:${FUCHSIA}; border-color:${FUCHSIA}; transform:translateY(0); }
.af-btn-sm{ height:38px; padding:0 18px; font-size:11px; }
.af-btn-ghost{ background:#fff; color:${INK} !important; border:1px solid ${INK}; box-shadow:none; }
.af-btn-ghost:hover:not(:disabled){ background:${TINT}; color:${PINK} !important; border-color:${PINK}; box-shadow:none; }
.af-btn-danger{ background:${INK}; border-color:${INK}; box-shadow:none; }
.af-btn-danger:hover:not(:disabled){ background:${GRAD_DARK}; border-color:${FUCHSIA}; }
.af-btn-ghost.af-btn-danger{ background:#fff; color:${FUCHSIA} !important; border-color:${FUCHSIA}; }
.af-btn-ghost.af-btn-danger:hover:not(:disabled){ background:${FUCHSIA}; color:#fff !important; border-color:${FUCHSIA}; }
.af-block{ width:100%; margin-top:12px; }
.af-iconbtn{ width:40px; height:40px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; color:${INK};
  background:#fff; border:1px solid ${LINE}; transition:border-color .2s, color .2s, transform .2s, background .2s; }
.af-iconbtn:hover{ border-color:${PINK}; background:${TINT}; color:${PINK}; transform:translateY(-1px); }

.af-input{ width:100%; padding:12px 14px; border-radius:12px; border:1px solid #D9D2D5; background:#fff;
  font-size:15px; font-weight:400; color:${INK}; outline:none; transition:border-color .2s, box-shadow .2s; }
.af-input::placeholder{ color:#A8A0A3; }
.af-input:focus{ border-color:${PINK}; box-shadow:0 0 0 3px rgba(250,40,100,.15); }
.op-area{ resize:vertical; min-height:96px; line-height:22px; }
.op-label{ display:block; font-size:11.5px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; color:${INK}; margin:18px 0 8px; }
.op-label i{ color:${PINK}; font-style:normal; margin-left:4px; }
.af-row > div > .op-label:first-child{ margin-top:18px; }

.op-genders{ display:flex; flex-wrap:wrap; gap:8px; }
.op-gender{ height:40px; padding:0 22px; border-radius:999px; border:1px solid ${INK}; background:#fff; color:${INK};
  font-size:12px; font-weight:500; letter-spacing:.18em; text-transform:uppercase; transition:all .2s; }
.op-gender:hover{ border-color:${PINK}; color:${PINK}; background:${TINT}; }
.op-gender.on{ background:${GRAD}; color:#fff; border-color:${PINK_DEEP}; }
.op-gender.on:hover{ background:${GRAD_DARK}; border-color:${FUCHSIA}; color:#fff; }

.op-attrs{ display:grid; grid-template-columns:1fr 1fr; gap:0 14px; }
.op-attrs .op-full{ grid-column:1 / -1; }
@media (max-width:520px){ .op-attrs{ grid-template-columns:1fr; } }
.op-swatch{ --c:#cccccc; position:relative; display:inline-block; width:24px; height:24px; border-radius:50%; flex-shrink:0;
  background:
    radial-gradient(circle at 30% 24%, #fff 0, rgba(255,255,255,.9) 6%, rgba(255,255,255,0) 26%),
    radial-gradient(circle at 36% 30%, color-mix(in srgb, var(--c) 72%, #fff) 0, var(--c) 48%, color-mix(in srgb, var(--c) 58%, #000) 100%);
  box-shadow: inset -2px -3px 5px rgba(0,0,0,.30), inset 1px 1px 2px rgba(255,255,255,.55), 0 0 0 1px rgba(0,0,0,.10), 0 3px 5px -1px rgba(0,0,0,.30); }
.op-swatch::after{ content:""; position:absolute; right:19%; bottom:15%; width:26%; height:14%; border-radius:50%; background:rgba(255,255,255,.35); transform:rotate(-38deg); filter:blur(.4px); }
.op-swatch.multi{ background:
    radial-gradient(circle at 30% 24%, #fff 0, rgba(255,255,255,.9) 6%, rgba(255,255,255,0) 26%),
    radial-gradient(circle at 50% 50%, rgba(255,255,255,0) 55%, rgba(0,0,0,.28) 100%),
    conic-gradient(#ff5c5c, #ffb347, #ffe94d, #5ddc6c, #4dd8ff, #6d7cff, #c86bff, #ff5c5c); }
.op-pickbtn .op-swatch{ margin-right:2px; }
.op-pick-opt .op-swatch{ width:28px; height:28px; }
.op-pickbtn .cur{ display:flex; align-items:center; gap:10px; min-width:0; }

.af-toolbar{ display:flex; align-items:center; gap:12px; padding:6px 16px; margin:16px 0; }
.af-toolbar:focus-within{ border-color:${PINK}; }
.af-search{ flex:1; display:flex; align-items:center; gap:10px; color:${BODY}; }
.af-search input{ flex:1; min-width:0; border:none; outline:none; background:transparent; font-size:15px; color:${INK}; height:40px; }
.af-search input::placeholder{ color:#A8A0A3; }
.af-empty{ text-align:center; padding:40px 12px; }
.af-emptycard{ display:flex; flex-direction:column; align-items:center; gap:10px; text-align:center; padding:44px 24px; margin-top:12px; }
.af-emptycard svg{ color:${PINK}; }
.af-emptycard h3{ margin:4px 0 0; }

.op-formgrid{ display:grid; grid-template-columns:1fr; gap:18px; align-items:start; }
@media (min-width:900px){ .op-formgrid{ grid-template-columns:1.25fr 1fr; } }
.op-col{ display:flex; flex-direction:column; gap:18px; }
.op-col > .af-card:first-child{ margin:0; }
@media (max-width:520px){ .af-row{ grid-template-columns:1fr; gap:0; } }

.op-photos{ display:grid; grid-template-columns:repeat(auto-fill, minmax(96px, 1fr)); gap:10px; }
.op-photo{ position:relative; aspect-ratio:1; border-radius:14px; overflow:hidden; background:#F7F3F5; border:1px solid ${LINE}; }
.op-photo img{ width:100%; height:100%; object-fit:cover; display:block; }
.op-photo-new{ outline:2px solid ${PINK}; outline-offset:-2px; }
.op-photo-tag{ position:absolute; left:6px; bottom:6px; font-size:9.5px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; color:#fff; background:${GRAD}; padding:3px 9px; border-radius:999px; }
.op-photo-x{ position:absolute; top:6px; right:6px; width:26px; height:26px; border-radius:50%; background:${INK}; color:#fff;
  display:flex; align-items:center; justify-content:center; transition:background .2s; }
.op-photo-x:hover{ background:${PINK}; }
.op-add-photo{ aspect-ratio:1; border-radius:14px; border:1px dashed ${INK}; background:#fff; color:${INK};
  display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; font-size:11px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; cursor:pointer; transition:all .2s; text-align:center; }
.op-add-photo:hover{ border-color:${PINK}; color:${PINK}; background:${TINT}; }
.op-spin{ animation:opSpin .8s linear infinite; }
@keyframes opSpin{ to{ transform:rotate(360deg); } }

.op-switch{ display:flex; align-items:center; gap:12px; width:100%; padding:12px 14px; border-radius:14px; background:#fff; border:1px solid #D9D2D5; text-align:left; transition:border-color .2s, background .2s; }
.op-switch:hover{ border-color:${PINK}; }
.op-switch.on{ background:${TINT}; border-color:${PINK}; }
.op-switch b{ font-size:14px; font-weight:500; }
.op-knob{ position:relative; width:46px; height:26px; border-radius:999px; background:#CFC8CB; flex-shrink:0; transition:background .2s; }
.op-knob::after{ content:""; position:absolute; top:3px; left:3px; width:20px; height:20px; border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.3); transition:transform .2s; }
.op-switch.on .op-knob{ background:${GRAD}; }
.op-switch.on .op-knob::after{ transform:translateX(20px); }

.op-stats{ display:grid; grid-template-columns:repeat(4, 1fr); gap:10px; margin-bottom:6px; }
@media (max-width:520px){ .op-stats{ grid-template-columns:repeat(2, 1fr); } }
.op-stat{ display:flex; flex-direction:column; align-items:flex-start; gap:2px; padding:16px 18px; text-align:left; transition:transform .2s, border-color .2s, background .2s, box-shadow .2s; }
.op-stat b{ font-family:'Cormorant Garamond',serif; font-size:40px; font-weight:500; line-height:1; color:${INK}; }
.op-stat span{ font-size:11px; font-weight:500; letter-spacing:.18em; text-transform:uppercase; color:${BODY}; margin-top:6px; }
.op-stat:hover{ border-color:${PINK}; transform:translateY(-2px); }
.op-stat.on{ background:${GRAD}; border-color:${PINK_DEEP}; box-shadow:0 18px 30px -18px ${FUCHSIA}; }
.op-stat.on b, .op-stat.on span{ color:#fff; }
.op-bulk{ display:flex; align-items:center; gap:8px; flex-wrap:wrap; padding:10px 14px; margin-bottom:12px; background:${TINT}; border-color:${TINT2}; }
.op-bulk b{ margin-right:auto; font-size:14px; font-weight:500; }
.op-list{ display:flex; flex-direction:column; gap:12px; margin-top:6px; }
.op-row{ display:flex; align-items:center; gap:14px; padding:12px 14px; transition:transform .2s, border-color .2s, background .2s; }
.op-row:hover{ border-color:${PINK}; }
.op-row.sel{ border-color:${PINK}; background:${TINT}; }
.op-check{ width:24px; height:24px; border-radius:7px; border:1px solid ${INK}; background:#fff; color:#fff; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.op-check.on{ background:${GRAD}; border-color:${PINK_DEEP}; }
.op-thumb{ width:64px; height:64px; border-radius:14px; overflow:hidden; display:flex; align-items:center; justify-content:center; flex-shrink:0; background:${TINT}; border:1px solid ${LINE}; color:${PINK}; }
.op-thumb img{ width:100%; height:100%; object-fit:cover; display:block; }
.op-name{ display:block; font-size:16px; font-weight:500; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.op-chips{ display:flex; flex-wrap:wrap; gap:6px; margin-top:8px; }
.op-chip{ display:inline-flex; align-items:center; gap:6px; font-size:10px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; padding:4px 10px; border-radius:999px; border:1px solid #D9D2D5; color:${BODY}; background:#fff; }
.op-chip .op-swatch{ width:14px; height:14px; box-shadow: inset -1px -1px 2px rgba(0,0,0,.28), inset 1px 1px 1px rgba(255,255,255,.5), 0 0 0 1px rgba(0,0,0,.10), 0 1px 2px rgba(0,0,0,.25); }
.op-chip .op-swatch::after{ display:none; }
.op-chip.live{ background:${GRAD}; border-color:${PINK_DEEP}; color:#fff; }
.op-chip.off{ background:#fff; border-color:#D9D2D5; color:#8A8387; }
.op-chip.out{ background:${INK}; border-color:${INK}; color:#fff; }
.op-chip.tag{ background:${TINT}; border-color:${TINT2}; color:${FUCHSIA}; }
.op-actions{ display:flex; gap:6px; flex-shrink:0; }
@media (max-width:520px){
  .op-row{ flex-wrap:wrap; }
  .op-actions{ width:100%; justify-content:flex-end; }
}

.op-pickbtn{ display:flex; align-items:center; justify-content:space-between; gap:8px; text-align:left; cursor:pointer; min-height:48px; }
.op-pickbtn:hover:not(:disabled){ border-color:${PINK}; }
.op-pickbtn span.lbl{ overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.op-pickbtn span.ph{ color:#A8A0A3; }
.op-pickbtn svg{ flex-shrink:0; color:${BODY}; }
.op-pick, .op-pick *, .op-pick *::before, .op-pick *::after{ box-sizing:border-box; font-family:'Jost',system-ui,sans-serif; }
.op-pick button{ font-family:inherit; cursor:pointer; border:none; background:none; color:inherit; }
.op-pick button:focus-visible{ outline:2px solid ${PINK}; outline-offset:2px; }
.op-pick-scrim{ position:fixed; inset:0; z-index:400; display:flex; background:rgba(0,0,0,.5); animation:opFade .18s ease-out; }
.op-pick-scrim.desktop{ align-items:center; justify-content:center; padding:20px; }
.op-pick-scrim.mobile{ align-items:flex-end; justify-content:center; }
.op-pick-sheet{ display:flex; flex-direction:column; width:100%; overflow:hidden; background:#fff; color:${INK}; }
.op-pick-sheet.desktop{ max-width:440px; max-height:72vh; border-radius:22px; box-shadow:0 30px 70px -20px rgba(220,0,60,.5); animation:opPop .22s cubic-bezier(.2,.9,.3,1.1); }
.op-pick-sheet.mobile{ max-height:80vh; border-radius:24px 24px 0 0; padding-bottom:env(safe-area-inset-bottom); box-shadow:0 -20px 50px -20px rgba(220,0,60,.45); animation:opSlide .26s cubic-bezier(.2,.9,.3,1); }
.op-pick-handle{ width:40px; height:4px; border-radius:999px; background:${TINT2}; margin:10px auto 0; flex-shrink:0; }
.op-pick-head{ display:flex; align-items:center; justify-content:space-between; gap:12px; padding:18px 20px 10px; flex-shrink:0; }
.op-pick-head h4{ margin:0; font-family:'Cormorant Garamond',serif; font-size:26px; font-weight:500; }
.op-pick-x{ width:36px; height:36px; border-radius:50%; border:1px solid ${LINE} !important; display:flex; align-items:center; justify-content:center; color:${INK}; transition:all .2s; }
.op-pick-x:hover{ border-color:${PINK} !important; color:${PINK}; background:${TINT} !important; }
.op-pick-list{ overflow-y:auto; padding:4px 14px 18px; display:flex; flex-direction:column; gap:6px; -webkit-overflow-scrolling:touch; }
.op-pick-opt{ display:flex; align-items:center; gap:12px; width:100%; padding:13px 14px; border-radius:14px; text-align:left; font-size:15px; font-weight:400; background:#fff; border:1px solid ${LINE} !important; transition:border-color .15s, color .15s, background .15s; }
.op-pick-opt:hover{ border-color:${PINK} !important; background:${TINT}; }
.op-pick-opt.on{ border-color:${PINK} !important; background:${TINT}; font-weight:500; }
.op-pick-opt .ico{ display:flex; align-items:center; color:${INK}; font-size:16px; flex-shrink:0; }
.op-pick-opt.on .ico{ color:${PINK}; }
.op-pick-opt .lbl{ flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.op-pick-opt .tick{ width:22px; height:22px; border-radius:50%; background:${GRAD}; color:#fff; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.op-pick-search{ display:flex; align-items:center; gap:10px; margin:2px 20px 10px; padding:0 14px; height:46px; border-radius:999px; border:1px solid ${INK}; background:#fff; flex-shrink:0; color:${BODY};
  transition:border-color .2s, box-shadow .2s; }
.op-pick-search:focus-within{ border-color:${PINK}; box-shadow:0 0 0 3px rgba(250,40,100,.15); color:${PINK}; }
.op-pick-search input{ flex:1; min-width:0; border:none; outline:none; background:transparent; font-size:15px; color:${INK}; height:100%; }
.op-pick-search input::placeholder{ color:#A8A0A3; }
.op-pick-clear{ width:24px; height:24px; border-radius:50%; background:${INK} !important; color:#fff !important; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:background .2s; }
.op-pick-clear:hover{ background:${PINK} !important; }
.op-pick-count{ font-size:10.5px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; color:${BODY}; padding:2px 6px 4px; }
.op-sel-chips{ display:flex; flex-wrap:wrap; gap:8px; margin-top:10px; }
.op-sel-chip{ display:inline-flex; align-items:center; gap:8px; height:34px; padding:0 6px 0 12px; border-radius:999px; border:1px solid ${PINK}; background:${TINT}; font-size:13px; font-weight:500; }
.op-sel-chip .op-swatch{ width:18px; height:18px; margin-left:-4px; }
.op-sel-chip button{ width:22px; height:22px; border-radius:50%; background:${INK}; color:#fff; display:flex; align-items:center; justify-content:center; transition:background .2s; }
.op-sel-chip button:hover{ background:${PINK}; }
.op-chip-colors .op-swatch + .op-swatch{ margin-left:-8px; }
.op-chip-colors > span:last-child{ margin-left:2px; }
.op-pick-tools{ display:flex; align-items:center; justify-content:space-between; gap:10px; padding:0 22px 8px; font-size:10.5px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; color:${BODY}; flex-shrink:0; }
.op-pick-tools-btns{ display:flex; gap:14px; }
.op-pick-tools-btns button{ font-size:10.5px; font-weight:500; letter-spacing:.16em; text-transform:uppercase; color:${INK}; padding:4px 0; transition:color .2s; }
.op-pick-tools-btns button:hover{ color:${PINK}; }
.op-pick-opt .box{ width:24px; height:24px; border-radius:8px; border:1px solid ${INK}; background:#fff; color:#fff; display:flex; align-items:center; justify-content:center; flex-shrink:0; transition:all .15s; }
.op-pick-opt .box.on{ background:${GRAD}; border-color:${PINK_DEEP}; }
.op-pick-foot{ padding:12px 20px calc(16px + env(safe-area-inset-bottom)); border-top:1px solid ${LINE}; flex-shrink:0; background:#fff; }
.op-pick-done{ width:100%; height:48px; border-radius:999px; background:${GRAD} !important; color:#fff !important; display:flex; align-items:center; justify-content:center; gap:8px;
  font-size:12px; font-weight:500; letter-spacing:.22em; text-transform:uppercase; box-shadow:0 12px 24px -14px ${FUCHSIA}; transition:background .25s, transform .25s; }
.op-pick-done:hover{ background:${GRAD_DARK} !important; transform:translateY(-1px); }
.op-variants{ display:flex; flex-direction:column; gap:16px; margin-top:14px; }
.op-variant{ padding:16px; border:1px solid ${INK}; border-radius:18px; background:#fff; }
.op-variant-head{ display:flex; align-items:center; gap:10px; margin-bottom:14px; }
.op-variant-head b{ font-family:'Cormorant Garamond',serif; font-size:22px; font-weight:500; flex:1; min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.op-variant .op-photos{ grid-template-columns:repeat(auto-fill, minmax(84px, 1fr)); }
.op-stockgrid{ display:grid; grid-template-columns:repeat(auto-fill, minmax(96px, 1fr)); gap:10px; margin-top:16px; }
.op-stock{ display:flex; flex-direction:column; gap:6px; }
.op-stock span{ font-size:11px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; color:${INK}; }
.op-stock .af-input{ padding:10px 12px; text-align:center; }
.op-pick-empty{ text-align:center; color:${BODY}; font-size:14px; padding:24px 12px; }
@keyframes opFade{ from{ opacity:0; } to{ opacity:1; } }
@keyframes opPop{ from{ opacity:0; transform:scale(.95) translateY(10px); } to{ opacity:1; transform:scale(1) translateY(0); } }
@keyframes opSlide{ from{ transform:translateY(100%); } to{ transform:translateY(0); } }
@media (prefers-reduced-motion: reduce){ .op-pick-scrim, .op-pick-sheet{ animation:none !important; } }

.op-modal{ position:fixed; inset:0; z-index:200; display:flex; align-items:center; justify-content:center; padding:20px; background:rgba(0,0,0,.5); }
.op-modal .af-card{ width:100%; max-width:400px; background:#fff; }
.op-modal .af-row{ margin-top:18px; gap:10px; }
.op-toast{ position:fixed; left:50%; bottom:calc(28px + env(safe-area-inset-bottom)); transform:translateX(-50%); z-index:500; max-width:calc(100% - 32px);
  background:${INK}; color:#fff; font-size:12px; font-weight:500; letter-spacing:.14em; text-transform:uppercase; padding:14px 24px; border-radius:999px; border-bottom:3px solid ${PINK}; box-shadow:0 14px 30px -10px rgba(220,0,60,.55);
  animation:opToast .25s ease-out; }
@keyframes opToast{ from{ opacity:0; transform:translate(-50%, 10px); } to{ opacity:1; transform:translate(-50%, 0); } }
@media (prefers-reduced-motion: reduce){ .op-toast, .op-spin{ animation:none !important; } .af-page *{ transition:none !important; } }
`;

/* ═══════════════════════════ PICKER: floating modal on desktop, bottom sheet on phones ═══════════════════════════ */
type PickOption = { value: string; label: string; icon?: React.ReactNode };

function useIsMobile(breakpoint = 700) {
  const [mobile, setMobile] = useState(typeof window !== "undefined" ? window.innerWidth < breakpoint : false);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return mobile;
}

const PickerField: React.FC<{
  label: string;
  title?: string;
  required?: boolean;
  value: string;
  options: PickOption[];
  onChange: (value: string) => void;
  placeholder?: string;
  emptyText?: string;
  disabled?: boolean;
}> = ({ label, title, required, value, options, onChange, placeholder = "Select", emptyText = "Nothing to pick yet.", disabled }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const isMobile = useIsMobile();
  const current = options.find((o) => o.value === value && o.value !== "");
  const searchable = options.length > 4;
  const needle = q.trim().toLowerCase();
  const visible = needle ? options.filter((o) => o.value !== "" && o.label.toLowerCase().includes(needle)) : options;

  useEffect(() => { if (!open) setQ(""); }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <>
      <label className="op-label">{label}{required && <i>*</i>}</label>
      <button type="button" disabled={disabled} className="af-input op-pickbtn" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
        <span className="cur">
          {current?.icon}
          <span className={`lbl ${current ? "" : "ph"}`}>{current ? current.label : placeholder}</span>
        </span>
        <ChevronDown size={16} strokeWidth={2} />
      </button>

      {open && createPortal(
        <div className={`op-pick op-pick-scrim ${isMobile ? "mobile" : "desktop"}`} onClick={() => setOpen(false)}>
          <div
            role="dialog" aria-modal="true" aria-label={title || label}
            className={`op-pick-sheet ${isMobile ? "mobile" : "desktop"}`}
            onClick={(e) => e.stopPropagation()}
          >
            {isMobile && <div className="op-pick-handle" />}
            <div className="op-pick-head">
              <h4>{title || label}</h4>
              <button type="button" className="op-pick-x" aria-label="Close" onClick={() => setOpen(false)}><X size={18} strokeWidth={2} /></button>
            </div>
            {searchable && (
              <div className="op-pick-search">
                <Search size={16} strokeWidth={2} />
                <input
                  type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}`}
                  autoFocus={!isMobile} aria-label={`Search ${label}`}
                />
                {q && <button type="button" className="op-pick-clear" aria-label="Clear search" onClick={() => setQ("")}><X size={14} strokeWidth={2.4} /></button>}
              </div>
            )}
            <div className="op-pick-list">
              {options.length === 0 && <p className="op-pick-empty">{emptyText}</p>}
              {options.length > 0 && visible.length === 0 && <p className="op-pick-empty">No match for “{q.trim()}”.</p>}
              {searchable && needle && visible.length > 0 && <p className="op-pick-count">{visible.length} found</p>}
              {visible.map((o) => {
                const on = o.value === value;
                return (
                  <button
                    type="button" key={o.value || "none"} className={`op-pick-opt${on ? " on" : ""}`} aria-pressed={on}
                    onClick={() => { onChange(o.value); setOpen(false); }}
                  >
                    {o.icon && <span className="ico">{o.icon}</span>}
                    <span className="lbl">{o.label}</span>
                    {on && <span className="tick"><Check size={13} strokeWidth={3} /></span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

/* ═══════════════════════════ MULTI PICKER: tick several, search, select all ═══════════════════════════ */
const MultiPickerField: React.FC<{
  label: string;
  title?: string;
  required?: boolean;
  values: string[];
  options: PickOption[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}> = ({ label, title, required, values, options, onChange, placeholder = "Select" }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const isMobile = useIsMobile();
  const needle = q.trim().toLowerCase();
  const visible = needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
  const searchable = options.length > 4;

  useEffect(() => { if (!open) setQ(""); }, [open]);
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [open]);

  const toggle = (v: string) => onChange(values.includes(v) ? values.filter((x) => x !== v) : [...values, v]);
  const allVisibleOn = visible.length > 0 && visible.every((o) => values.includes(o.value));
  const selectVisible = () => onChange(Array.from(new Set([...values, ...visible.map((o) => o.value)])));
  const unselectVisible = () => onChange(values.filter((v) => !visible.some((o) => o.value === v)));

  return (
    <>
      <label className="op-label">{label}{required && <i>*</i>}</label>
      <button type="button" className="af-input op-pickbtn" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
        <span className="cur">
          <span className={`lbl ${values.length ? "" : "ph"}`}>{values.length ? `${values.length} selected` : placeholder}</span>
        </span>
        <ChevronDown size={16} strokeWidth={2} />
      </button>

      {values.length > 0 && (
        <div className="op-sel-chips">
          {values.map((v) => {
            const icon = options.find((o) => o.value === v)?.icon;
            return (
              <span key={v} className="op-sel-chip">
                {icon}
                <span>{v}</span>
                <button type="button" aria-label={`Remove ${v}`} onClick={() => toggle(v)}><X size={12} strokeWidth={2.6} /></button>
              </span>
            );
          })}
        </div>
      )}

      {open && createPortal(
        <div className={`op-pick op-pick-scrim ${isMobile ? "mobile" : "desktop"}`} onClick={() => setOpen(false)}>
          <div
            role="dialog" aria-modal="true" aria-label={title || label}
            className={`op-pick-sheet ${isMobile ? "mobile" : "desktop"}`}
            onClick={(e) => e.stopPropagation()}
          >
            {isMobile && <div className="op-pick-handle" />}
            <div className="op-pick-head">
              <h4>{title || label}</h4>
              <button type="button" className="op-pick-x" aria-label="Close" onClick={() => setOpen(false)}><X size={18} strokeWidth={2} /></button>
            </div>

            {searchable && (
              <div className="op-pick-search">
                <Search size={16} strokeWidth={2} />
                <input
                  type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${label.toLowerCase()}`}
                  autoFocus={!isMobile} aria-label={`Search ${label}`}
                />
                {q && <button type="button" className="op-pick-clear" aria-label="Clear search" onClick={() => setQ("")}><X size={14} strokeWidth={2.4} /></button>}
              </div>
            )}

            <div className="op-pick-tools">
              <span>{values.length} selected</span>
              <span className="op-pick-tools-btns">
                {visible.length > 1 && (
                  <button type="button" onClick={allVisibleOn ? unselectVisible : selectVisible}>
                    {allVisibleOn ? "Unselect" : needle ? "Select found" : "Select all"}
                  </button>
                )}
                {values.length > 0 && <button type="button" onClick={() => onChange([])}>Clear</button>}
              </span>
            </div>

            <div className="op-pick-list">
              {options.length === 0 && <p className="op-pick-empty">Nothing to pick yet.</p>}
              {options.length > 0 && visible.length === 0 && <p className="op-pick-empty">No match for “{q.trim()}”.</p>}
              {visible.map((o) => {
                const on = values.includes(o.value);
                return (
                  <button type="button" key={o.value} className={`op-pick-opt${on ? " on" : ""}`} aria-pressed={on} onClick={() => toggle(o.value)}>
                    {o.icon && <span className="ico">{o.icon}</span>}
                    <span className="lbl">{o.label}</span>
                    <span className={`box${on ? " on" : ""}`}>{on && <Check size={13} strokeWidth={3} />}</span>
                  </button>
                );
              })}
            </div>

            <div className="op-pick-foot">
              <button type="button" className="op-pick-done" onClick={() => setOpen(false)}>
                <Check size={15} /> Done{values.length ? ` (${values.length})` : ""}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

/* ═══════════════════════════ ONE ATTRIBUTE FIELD (select / multiselect / text / number / color) ═══════════════════════════ */
const AttributeField: React.FC<{
  attr: Attribute;
  value: AttrValue | undefined;
  onChange: (v: AttrValue) => void;
}> = ({ attr, value, onChange }) => {
  const type = attr.inputType ?? "select";
  const options = attr.options ?? [];

  /* Color: pick as many as you sell */
  if (MULTI_ATTRS.includes(attr.name) || type === "color") {
    const isColor = type === "color";
    return (
      <div className="op-full">
        <MultiPickerField
          label={attr.name} title={`Choose ${attr.name.toLowerCase()}s`} required={attr.required}
          values={toArr(value)} placeholder={`Select ${attr.name.toLowerCase()}s`}
          options={options.map((o) => ({ value: optValue(o), label: optLabel(o), icon: isColor ? swatch(o.hex) : undefined }))}
          onChange={(v) => onChange(v)}
        />
      </div>
    );
  }

  if (type === "multiselect") {
    const list = Array.isArray(value) ? value : [];
    return (
      <div className="op-full">
        <label className="op-label">{attr.name}{attr.required && <i>*</i>}</label>
        <div className="op-genders">
          {options.map((o) => {
            const v = optValue(o);
            const on = list.includes(v);
            return (
              <button
                type="button" key={o.id} className={`op-gender ${on ? "on" : ""}`} aria-pressed={on}
                onClick={() => onChange(on ? list.filter((x) => x !== v) : [...list, v])}
              >
                {optLabel(o)}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (type === "text" || type === "number") {
    return (
      <div>
        <label className="op-label">{attr.name}{attr.unit ? ` (${attr.unit})` : ""}{attr.required && <i>*</i>}</label>
        <input
          className="af-input" type={type === "number" ? "number" : "text"} min={type === "number" ? 0 : undefined}
          value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)}
        />
      </div>
    );
  }

  /* select + color */
  return (
    <div>
      <PickerField
        label={attr.name} title={`Choose ${attr.name.toLowerCase()}`} required={attr.required}
        value={typeof value === "string" ? value : ""} placeholder="None"
        options={[
          { value: "", label: "None" },
          ...options.map((o) => ({ value: optValue(o), label: optLabel(o) })),
        ]}
        onChange={(v) => onChange(v)}
      />
    </div>
  );
};

/* ═══════════════════════════ FORM STATE ═══════════════════════════ */
type VariantForm = { images: string[]; stock: Record<string, string> };
const NO_SIZE = "-"; /* stock key used when a product has no size */

type FormState = {
  name: string;
  description: string;
  price: string;
  quantity: string;
  categoryId: string;
  attributes: AttrMap;
  variants: Record<string, VariantForm>;
  country: string;
  city: string;
  published: boolean;
  images: string[];
};

const emptyForm = (): FormState => ({
  name: "", description: "", price: "", quantity: "", categoryId: "", attributes: {}, variants: {},
  country: "", city: "", published: true, images: [],
});

const toForm = (p: Product): FormState => {
  const cat = String(p.categoryId ?? "");
  return {
    name: p.name || "", description: p.description || "", price: String(p.price ?? ""), quantity: String(p.quantity ?? ""),
    categoryId: byId(cat) ? cat : "",
    attributes: { ...((p as any).attributes || {}) },
    variants: Object.fromEntries(
      (((p as any).variants || []) as any[]).map((v) => [
        v.color,
        { images: v.images || [], stock: Object.fromEntries((v.stock || []).map((x: any) => [x.size, String(x.qty ?? 0)])) },
      ])
    ),
    country: p.country || "", city: p.city || "", published: p.published !== false, images: p.images || [],
  };
};

const attrText = (p: Product) =>
  Object.values(((p as any).attributes || {}) as AttrMap).flat().join(" ").toLowerCase();

const previewOf = (p: Product): string | undefined =>
  p.images?.[0] || (((p as any).variants || []) as any[]).flatMap((v) => v.images || [])[0];

export default function Products() {
  const dispatch = useDispatch<AppDispatch>();
  const { ownerProducts, ownerProductsLoading } = useSelector((s: RootState) => s.products);

  const [view, setView] = useState<"list" | "form">("list");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [pending, setPending] = useState<{ file: File; preview: string }[]>([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "active" | "draft" | "out">("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [removingUrl, setRemovingUrl] = useState<string | null>(null);
  const [pendingV, setPendingV] = useState<Record<string, { file: File; preview: string }[]>>({});
  const origVariantUrls = useRef<string[]>([]);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  const stats = useMemo(() => ({
    total: ownerProducts.length,
    active: ownerProducts.filter((p) => p.published !== false).length,
    draft: ownerProducts.filter((p) => p.published === false).length,
    out: ownerProducts.filter((p) => p.quantity <= 0).length,
  }), [ownerProducts]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ownerProducts.filter((p) => {
      if (filter === "active" && p.published === false) return false;
      if (filter === "draft" && p.published !== false) return false;
      if (filter === "out" && p.quantity > 0) return false;
      if (!q) return true;
      const cat = pathLabel(p.categoryId).toLowerCase();
      return p.name.toLowerCase().includes(q) || cat.includes(q) || attrText(p).includes(q);
    });
  }, [ownerProducts, query, filter]);

  const formAttrs = useMemo(
    () =>
      attrsFor(form.categoryId).map((a) =>
        a.name === "Brand" ? ({ ...a, required: false, inputType: "text", options: [] } as Attribute) : a
      ),
    [form.categoryId]
  );

  const colors = toArr(form.attributes["Color"]);
  const stockKeys = [NO_SIZE]; /* beauty: stock per color / shade only */
  const trail = trailOf(form.categoryId);
  const hasVariants = colors.length > 0;
  const qtyOf = (c: string, k: string) => parseInt(form.variants[c]?.stock?.[k] || "0", 10) || 0;
  const variantTotal = colors.reduce((sum, c) => sum + stockKeys.reduce((s2, k) => s2 + qtyOf(c, k), 0), 0);

  const clearPendingV = () => { Object.values(pendingV).flat().forEach((x) => URL.revokeObjectURL(x.preview)); setPendingV({}); };
  const clearPending = () => { pending.forEach((x) => URL.revokeObjectURL(x.preview)); setPending([]); clearPendingV(); };
  const openNew = () => { clearPending(); origVariantUrls.current = []; setEditingId(null); setForm(emptyForm()); setView("form"); window.scrollTo({ top: 0 }); };
  const openEdit = (p: Product) => {
    clearPending();
    origVariantUrls.current = (((p as any).variants || []) as any[]).flatMap((v) => v.images || []);
    setEditingId(p.productId); setForm(toForm(p)); setView("form"); window.scrollTo({ top: 0 });
  };
  const closeForm = () => { clearPending(); setView("list"); setEditingId(null); };
  const pickCategory = (id: string) =>
    setForm((f) => {
      const keep = new Set(attrsFor(id).map((a) => a.name));
      return { ...f, categoryId: id, attributes: Object.fromEntries(Object.entries(f.attributes).filter(([k]) => keep.has(k))) };
    });

  /* per-color photos + stock */
  const setStock = (color: string, size: string, val: string) =>
    setForm((f) => ({ ...f, variants: { ...f.variants, [color]: { images: f.variants[color]?.images || [], stock: { ...(f.variants[color]?.stock || {}), [size]: val } } } }));
  const addVariantFiles = (color: string, e: React.ChangeEvent<HTMLInputElement>) => {
    const files = (Array.from(e.target.files || []) as File[]).filter((f) => f.type.startsWith("image/"));
    setPendingV((prev) => ({ ...prev, [color]: [...(prev[color] || []), ...files.map((file) => ({ file, preview: URL.createObjectURL(file) }))] }));
    e.target.value = "";
  };
  const removePendingV = (color: string, i: number) => {
    const item = pendingV[color]?.[i];
    if (item) URL.revokeObjectURL(item.preview);
    setPendingV((prev) => ({ ...prev, [color]: (prev[color] || []).filter((_, k) => k !== i) }));
  };
  /* saved color photos are removed from the product here and deleted from Storage on save */
  const removeSavedV = (color: string, url: string) =>
    setForm((f) => ({ ...f, variants: { ...f.variants, [color]: { images: (f.variants[color]?.images || []).filter((u) => u !== url), stock: f.variants[color]?.stock || {} } } }));

  const pickFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = (Array.from(e.target.files || []) as File[]).filter((f) => f.type.startsWith("image/"));
    setPending((prev) => [...prev, ...files.map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
    e.target.value = "";
  };

  const removePending = (i: number) => {
    URL.revokeObjectURL(pending[i].preview);
    setPending((prev) => prev.filter((_, k) => k !== i));
  };

  const removeSaved = async (url: string) => {
    if (!editingId) return;
    setRemovingUrl(url);
    try {
      await dispatch(deleteProductMedia({ productId: editingId, photoUrl: url })).unwrap();
      setForm((f) => ({ ...f, images: f.images.filter((u) => u !== url) }));
      setToast("Photo removed");
    } catch (err: any) {
      setToast(err?.message || String(err) || "Could not remove photo");
    } finally {
      setRemovingUrl(null);
    }
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) return setToast("Give the product a name first");
    if (!byId(form.categoryId)) return setToast("Choose a category");
    if (childrenOf(form.categoryId).length) return setToast("Choose a subcategory");
    const price = Number(form.price);
    const quantity = hasVariants ? variantTotal : Number(form.quantity);
    if (!(price >= 0) || form.price === "") return setToast("Enter a price");
    if (!Number.isInteger(quantity) || quantity < 0) return setToast("Stock must be a whole number");

    if (hasVariants) {
      const bad = colors.some((c) => stockKeys.some((k) => { const raw = form.variants[c]?.stock?.[k]; return raw != null && raw !== "" && !(Number.isInteger(Number(raw)) && Number(raw) >= 0); }));
      if (bad) return setToast("Stock must be whole numbers");
    }

    const missing = formAttrs.find((a) => a.required && isEmpty(form.attributes[a.name]));
    if (missing) return setToast(`Choose ${missing.name.toLowerCase()}`);

    /* keep only values for attributes that exist in this category, drop empties */
    const attributes: AttrMap = {};
    formAttrs.forEach((a) => {
      const v = form.attributes[a.name];
      if (!isEmpty(v)) attributes[a.name] = Array.isArray(v) ? v : String(v).trim();
    });

    const variantsData = hasVariants
      ? colors.map((c) => ({
          color: c,
          hex: hexOf(c) || null,
          images: form.variants[c]?.images || [],
          stock: stockKeys.map((k) => ({ size: k, qty: qtyOf(c, k) })),
        }))
      : [];

    setSaving(true);
    try {
      const saved = await dispatch(
        upsertProduct({
          productId: editingId || undefined,
          data: {
            name: form.name.trim(),
            description: form.description.trim(),
            price,
            quantity,
            categoryId: form.categoryId,
            attributes,
            variants: variantsData,
            country: form.country.trim() || null,
            city: form.city.trim() || null,
            published: form.published,
          } as any,
        })
      ).unwrap();

      /* upload each color's new photos, then store their urls on that color */
      if (hasVariants && colors.some((c) => (pendingV[c] || []).length)) {
        const uploaded = await Promise.all(
          colors.map((c) => {
            const files = (pendingV[c] || []).map((x) => x.file);
            return files.length ? dispatch(uploadProductFiles({ productId: saved.productId, files, tag: c })).unwrap() : Promise.resolve([] as string[]);
          })
        );
        const merged = variantsData.map((v, i) => ({ ...v, images: [...v.images, ...uploaded[i]] }));
        await dispatch(upsertProduct({ productId: saved.productId, data: { variants: merged } as any })).unwrap();
      }

      /* delete color photos the seller removed */
      const kept = new Set(variantsData.flatMap((v) => v.images));
      const orphans = origVariantUrls.current.filter((u) => !kept.has(u));
      if (orphans.length) await dispatch(deleteProductFiles(orphans)).unwrap().catch(() => {});

      if (pending.length) {
        await dispatch(upsertProductMedia({ productId: saved.productId, files: pending.map((x) => x.file) })).unwrap();
      }
      clearPending();
      setToast(editingId ? "Product updated" : "Product created");
      setView("list");
      setEditingId(null);
    } catch (err: any) {
      setToast(err?.message || String(err) || "Could not save product");
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (p: Product) => {
    try {
      await dispatch(upsertProduct({ productId: p.productId, data: { published: p.published === false } })).unwrap();
      setToast(p.published === false ? "Product is live" : "Product hidden");
    } catch (err: any) { setToast(String(err)); }
  };

  const remove = async (id: string) => {
    await dispatch(deleteProduct(id));
    setConfirmId(null);
    setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
    setToast("Product deleted");
    if (editingId === id) closeForm();
  };

  const toggleSel = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setSelected((s) => (s.size === shown.length ? new Set() : new Set(shown.map((p) => p.productId))));

  const bulkPublish = async (published: boolean) => {
    await Promise.all(Array.from(selected).map((id) => dispatch(upsertProduct({ productId: id, data: { published } })).unwrap().catch(() => {})));
    setToast(published ? "Products are live" : "Products hidden");
    setSelected(new Set());
  };

  const bulkDelete = async () => {
    await Promise.all(Array.from(selected).map((id) => dispatch(deleteProduct(id))));
    setToast("Products deleted");
    setSelected(new Set());
  };

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const setAttr = (name: string, v: AttrValue) => setForm((f) => ({ ...f, attributes: { ...f.attributes, [name]: v } }));


  const deleteModal = confirmId && (
    <div className="op-modal" onClick={() => setConfirmId(null)}>
      <div className="af-glass af-card" onClick={(e) => e.stopPropagation()}>
        <h3>Delete this product?</h3>
        <p className="af-muted">It disappears from the shop and its photos are removed.</p>
        <div className="af-row">
          <button className="af-btn af-btn-ghost" onClick={() => setConfirmId(null)}>Keep it</button>
          <button className="af-btn af-btn-danger" onClick={() => remove(confirmId)}>Delete</button>
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════ FORM VIEW ═══════════════════════════ */
  if (view === "form") {
    const allPhotos = form.images.length + pending.length;
    return (
      <div className="af-page">
        <style>{CSS}</style>
        <div className="af-wrap op-wide">
          <button className="af-link" onClick={closeForm}><ArrowLeft size={16} /> Back to products</button>
          <h2>{editingId ? "Edit product" : "New product"}</h2>

          <form onSubmit={save} className="op-formgrid">
            <div className="op-col">
              <div className="af-glass af-card">
                <h3>Details</h3>
                <label className="op-label" style={{ marginTop: 0 }}>Name</label>
                <input className="af-input" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Hydrating rose face serum 30 ml" required />

                <label className="op-label">Description</label>
                <textarea className="af-input op-area" value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Key ingredients, how to use, what's in the box" rows={4} />
                <div className="af-row">
                  <div><label className="op-label">Price (€)</label><input className="af-input" type="number" min="0" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} required /></div>
                  <div><label className="op-label">{hasVariants ? "Total stock" : "Stock"}</label><input className="af-input" type="number" min="0" step="1" value={hasVariants ? String(variantTotal) : form.quantity} onChange={(e) => set("quantity", e.target.value)} required={!hasVariants} disabled={hasVariants} title={hasVariants ? "Adds up the stock of every color" : undefined} /></div>
                </div>

                <PickerField
                  label="Category" title="Choose a category" required value={trail[0]?.id ?? ""} placeholder="Choose a category"
                  options={CATEGORIES.map((c) => ({ value: c.id, label: c.name, icon: c.icon }))}
                  onChange={pickCategory}
                />
                {!!trail[0] && childrenOf(trail[0].id).length > 0 && (
                  <PickerField
                    label="Subcategory" title="Choose a subcategory" required value={trail[1]?.id ?? ""} placeholder="Choose a subcategory"
                    options={childrenOf(trail[0].id).map((c) => ({ value: c.id, label: c.name, icon: c.icon }))}
                    onChange={pickCategory}
                  />
                )}
                {!!trail[1] && childrenOf(trail[1].id).length > 0 && (
                  <PickerField
                    label="Type" title="Choose a type" required value={trail[2]?.id ?? ""} placeholder="Choose a type"
                    options={childrenOf(trail[1].id).map((c) => ({ value: c.id, label: c.name, icon: c.icon }))}
                    onChange={pickCategory}
                  />
                )}

                <div className="af-row">
                  <div><label className="op-label">Country</label><input className="af-input" value={form.country} onChange={(e) => set("country", e.target.value)} placeholder="Blank = everywhere" /></div>
                  <div><label className="op-label">City</label><input className="af-input" value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Optional" /></div>
                </div>
              </div>

              {formAttrs.length > 0 && (
                <div className="af-glass af-card">
                  <h3>{nameOf(form.categoryId) || "Product"} options</h3>
                  <div className="op-attrs">
                    {formAttrs.map((a) => (
                      <AttributeField key={`${form.categoryId}-${a.id}-${a.name}`} attr={a} value={form.attributes[a.name]} onChange={(v) => setAttr(a.name, v)} />
                    ))}
                  </div>
                </div>
              )}

              {hasVariants && (
                <div className="af-glass af-card">
                  <h3>Colors &amp; stock</h3>
                  <p className="af-muted af-small">Add photos and stock for each color.</p>
                  <div className="op-variants">
                    {colors.map((c) => {
                      const v = form.variants[c] || { images: [], stock: {} };
                      const pv = pendingV[c] || [];
                      return (
                        <div key={c} className="op-variant">
                          <div className="op-variant-head">
                            {swatch(hexOf(c))}
                            <b>{c}</b>
                            <span className="af-muted af-small">{v.images.length + pv.length} photos</span>
                          </div>

                          <div className="op-photos">
                            {v.images.map((url) => (
                              <div key={url} className="op-photo">
                                <img src={url} alt="" />
                                <button type="button" className="op-photo-x" aria-label="Remove photo" onClick={() => removeSavedV(c, url)}><X size={14} /></button>
                              </div>
                            ))}
                            {pv.map((x, i) => (
                              <div key={x.preview} className="op-photo op-photo-new">
                                <img src={x.preview} alt="" />
                                <span className="op-photo-tag">New</span>
                                <button type="button" className="op-photo-x" aria-label="Remove photo" onClick={() => removePendingV(c, i)}><X size={14} /></button>
                              </div>
                            ))}
                            <label className="op-add-photo">
                              <ImagePlus size={22} strokeWidth={1.6} /><span>{c} photos</span>
                              <input type="file" accept="image/*" multiple hidden onChange={(e) => addVariantFiles(c, e)} />
                            </label>
                          </div>

                          <div className="op-stockgrid">
                            {stockKeys.map((k) => (
                              <label key={k} className="op-stock">
                                <span>{k === NO_SIZE ? "Stock" : k}</span>
                                <input
                                  className="af-input" type="number" min="0" step="1" placeholder="0"
                                  value={v.stock[k] ?? ""} onChange={(e) => setStock(c, k, e.target.value)}
                                />
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="op-col">
              <div className="af-glass af-card">
                <h3>{hasVariants ? "Main photos" : "Photos"} <span className="af-muted af-small">({allPhotos})</span></h3>
                <div className="op-photos">
                  {form.images.map((url) => (
                    <div key={url} className="op-photo">
                      <img src={url} alt="" />
                      <button type="button" className="op-photo-x" aria-label="Remove photo" onClick={() => removeSaved(url)} disabled={removingUrl === url}>
                        {removingUrl === url ? <Loader2 size={14} className="op-spin" /> : <X size={14} />}
                      </button>
                    </div>
                  ))}
                  {pending.map((x, i) => (
                    <div key={x.preview} className="op-photo op-photo-new">
                      <img src={x.preview} alt="" />
                      <span className="op-photo-tag">New</span>
                      <button type="button" className="op-photo-x" aria-label="Remove photo" onClick={() => removePending(i)}><X size={14} /></button>
                    </div>
                  ))}
                  <label className="op-add-photo">
                    <ImagePlus size={24} strokeWidth={1.6} /><span>Add photos</span>
                    <input type="file" accept="image/*" multiple hidden onChange={pickFiles} />
                  </label>
                </div>
                {!editingId && <p className="af-muted af-small" style={{ marginTop: 10 }}>Photos upload when you save. The first photo shows on the home page.</p>}
              </div>

              <div className="af-glass af-card">
                <h3>Visibility</h3>
                <button type="button" className={`op-switch ${form.published ? "on" : ""}`} onClick={() => set("published", !form.published)} aria-pressed={form.published}>
                  <span className="op-knob" />
                  <b>{form.published ? "Live in the shop" : "Hidden draft"}</b>
                </button>
                <button className="af-btn af-block" disabled={saving}>
                  {saving ? <><Loader2 size={16} className="op-spin" /> Saving</> : <><Check size={16} /> {editingId ? "Save changes" : "Create product"}</>}
                </button>
                {editingId && (
                  <button type="button" className="af-btn af-btn-ghost af-btn-danger af-block" onClick={() => setConfirmId(editingId)}><Trash2 size={16} /> Delete product</button>
                )}
              </div>
            </div>
          </form>
        </div>

        {deleteModal}
        {toast && <div className="op-toast" role="status">{toast}</div>}
      </div>
    );
  }

  /* ═══════════════════════════ LIST VIEW ═══════════════════════════ */
  return (
    <div className="af-page">
      <style>{CSS}</style>
      <div className="af-wrap op-wide">
        <div className="af-between">
          <h2 style={{ margin: 0 }}>Products</h2>
          <button className="af-btn af-btn-sm" onClick={openNew}><Plus size={16} /> New product</button>
        </div>
        <div style={{ height: 18 }} />

        <div className="op-stats">
          {([["all", "All", stats.total], ["active", "Live", stats.active], ["draft", "Hidden", stats.draft], ["out", "Sold out", stats.out]] as const).map(([k, label, n]) => (
            <button key={k} className={`op-stat af-glass ${filter === k ? "on" : ""}`} onClick={() => setFilter(k)}>
              <b>{n}</b><span>{label}</span>
            </button>
          ))}
        </div>

        <div className="af-toolbar af-glass">
          <label className="af-search"><Search size={18} strokeWidth={1.8} /><input placeholder="Search products, brands, categories" value={query} onChange={(e) => setQuery(e.target.value)} /></label>
          {shown.length > 0 && <button className="af-link" onClick={toggleAll}>{selected.size === shown.length ? "Clear" : "Select all"}</button>}
        </div>

        {selected.size > 0 && (
          <div className="op-bulk af-glass">
            <b>{selected.size} selected</b>
            <button className="af-btn af-btn-sm af-btn-ghost" onClick={() => bulkPublish(true)}><Eye size={14} /> Go live</button>
            <button className="af-btn af-btn-sm af-btn-ghost" onClick={() => bulkPublish(false)}><EyeOff size={14} /> Hide</button>
            <button className="af-btn af-btn-sm af-btn-danger" onClick={bulkDelete}><Trash2 size={14} /> Delete</button>
          </div>
        )}

        {ownerProductsLoading && !ownerProducts.length && <p className="af-muted af-empty">Loading your products</p>}
        {!ownerProductsLoading && shown.length === 0 && (
          <div className="af-glass af-emptycard">
            <PackageOpen size={44} strokeWidth={1.4} />
            <h3>{ownerProducts.length ? "No matches" : "No products yet"}</h3>
            <p className="af-muted">{ownerProducts.length ? "Try another filter." : "Add your first product to start selling."}</p>
            {!ownerProducts.length && <button className="af-btn" onClick={openNew}><Plus size={16} /> New product</button>}
          </div>
        )}

        <div className="op-list">
          {shown.map((p) => {
            const hidden = p.published === false;
            const cat = pathLabel(p.categoryId);
            const attrs = ((p as any).attributes || {}) as AttrMap;
            const brand = typeof attrs["Brand"] === "string" ? attrs["Brand"] : "";
            const tagKey = TAG_ATTRS.find((k) => typeof attrs[k] === "string" && attrs[k]);
            const tag = tagKey ? (attrs[tagKey] as string) : "";
            const colors = toArr(attrs["Color"]);
            const preview = previewOf(p);
            return (
              <div key={p.productId} className={`op-row af-glass ${selected.has(p.productId) ? "sel" : ""}`}>
                <button className={`op-check ${selected.has(p.productId) ? "on" : ""}`} onClick={() => toggleSel(p.productId)} aria-label="Select">{selected.has(p.productId) && <Check size={14} />}</button>
                <div className="op-thumb">
                  {preview ? <img src={preview} alt="" /> : <PackageOpen size={22} strokeWidth={1.5} />}
                </div>
                <div className="af-grow">
                  <b className="op-name">{p.name}</b>
                  <div className="af-muted af-small">€{p.price.toFixed(2)}{brand ? ` · ${brand}` : ""}{cat ? ` · ${cat}` : ""}</div>
                  <div className="op-chips">
                    <span className={`op-chip ${hidden ? "off" : "live"}`}>{hidden ? "Hidden" : "Live"}</span>
                    <span className={`op-chip ${p.quantity <= 0 ? "out" : ""}`}>{p.quantity <= 0 ? "Sold out" : `${p.quantity} in stock`}</span>
                    {tag && <span className="op-chip tag">{tag}</span>}
                    {colors.length > 0 && (
                      <span className="op-chip op-chip-colors">
                        {colors.slice(0, 6).map((c) => <React.Fragment key={c}>{swatch(hexOf(c))}</React.Fragment>)}
                        <span>{colors.length === 1 ? colors[0] : `${colors.length} colors`}</span>
                      </span>
                    )}
                  </div>
                </div>
                <div className="op-actions">
                  <button className="af-iconbtn" aria-label={hidden ? "Go live" : "Hide"} onClick={() => togglePublished(p)}>{hidden ? <Eye size={18} strokeWidth={1.8} /> : <EyeOff size={18} strokeWidth={1.8} />}</button>
                  <button className="af-iconbtn" aria-label="Edit" onClick={() => openEdit(p)}><Pencil size={18} strokeWidth={1.8} /></button>
                  <button className="af-iconbtn" aria-label="Delete" onClick={() => setConfirmId(p.productId)}><Trash2 size={18} strokeWidth={1.8} /></button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {deleteModal}
      {toast && <div className="op-toast" role="status">{toast}</div>}
    </div>
  );
}